import { cents, sumCents } from '../money/cents';
import type { Cents } from '../money/cents';

/**
 * The invoice three-way match (SCOPE §6.3, decision 128).
 *
 * The client's words: "they get an invoice after RO and the prices on invoice
 * should match the prices on PO (since there are discounts set at PO that
 * sometimes get lost by mistake via fulfillment when invoice shows up)."
 *
 * Three documents, two questions per line:
 *
 *  - **Price**: did the vendor bill each unit at the cost the order agreed?
 *    (invoiced cost − ordered cost) × units billed. A lost discount shows
 *    here, and it is the money the shop asks back.
 *  - **Quantity**: did the vendor bill for units that never arrived?
 *    (units billed − units received) × invoiced cost. Billing for a backorder
 *    still on a truck shows here.
 *
 * Over- and under-billing are summed separately, never netted: being
 * under-billed on one style is not the vendor's credit against over-billing
 * on another, and a report that nets them hides both.
 *
 * Integer cents throughout; the only arithmetic is an integer quantity times
 * an integer cost, so nothing here rounds.
 */

export interface InvoiceLineInput {
  /** Units the purchase order line received, all deliveries to date. */
  readonly receivedQuantity: number;
  /** Cost each the order agreed. */
  readonly orderedCostCents: Cents;
  /** Units the invoice bills. */
  readonly invoicedQuantity: number;
  /** Cost each the invoice bills. */
  readonly invoicedCostCents: Cents;
}

export interface InvoiceLineVariance {
  /** Positive: billed above the agreed cost. Negative: below it. */
  readonly priceVarianceCents: Cents;
  /** Positive: billed for units not received. Negative: received units not billed. */
  readonly quantityVarianceCents: Cents;
  /** What the invoice line bills in total. */
  readonly billedCents: Cents;
  /** Price and quantity together, for this line. */
  readonly varianceCents: Cents;
}

export interface InvoiceVariance {
  readonly lines: readonly InvoiceLineVariance[];
  /** What the invoice bills, all lines. */
  readonly billedCents: Cents;
  /** Over-billing only, per component, summed: the money to ask back. */
  readonly overBilledCents: Cents;
  /** Under-billing only, as a positive figure, kept apart rather than netted. */
  readonly underBilledCents: Cents;
  /** Every line agrees with the order and the delivery. */
  readonly matched: boolean;
}

function assertWhole(value: number, what: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${what} must be a whole number, zero or more`);
  }
}

export function invoiceLineVariance(line: InvoiceLineInput): InvoiceLineVariance {
  assertWhole(line.receivedQuantity, 'received quantity');
  assertWhole(line.invoicedQuantity, 'invoiced quantity');
  assertWhole(line.orderedCostCents, 'ordered cost');
  assertWhole(line.invoicedCostCents, 'invoiced cost');

  const price = cents((line.invoicedCostCents - line.orderedCostCents) * line.invoicedQuantity);
  const quantity = cents((line.invoicedQuantity - line.receivedQuantity) * line.invoicedCostCents);
  return {
    priceVarianceCents: price,
    quantityVarianceCents: quantity,
    billedCents: cents(line.invoicedQuantity * line.invoicedCostCents),
    varianceCents: cents(price + quantity),
  };
}

export function invoiceVariance(lines: readonly InvoiceLineInput[]): InvoiceVariance {
  const each = lines.map(invoiceLineVariance);
  /*
   * Split per component, not per line: a line billed above cost and short on
   * units is over on price and under on quantity, and both are real. Netting
   * them inside the line would hide a lost discount behind an unbilled unit.
   */
  const parts = each.flatMap((line) => [line.priceVarianceCents, line.quantityVarianceCents]);
  return {
    lines: each,
    billedCents: sumCents(each.map((line) => line.billedCents)),
    overBilledCents: sumCents(parts.map((part) => cents(Math.max(part, 0)))),
    underBilledCents: sumCents(parts.map((part) => cents(Math.max(-part, 0)))),
    matched: parts.every((part) => part === 0),
  };
}
